import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useFeature, useViewer, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Label, Paragraph, Radio, Select, Spinner, Subheading } from '@treams/treams-ui';

type ConversationPartnerSelectProps = {
	moment?: boolean;
	titlePrefix?: string;
	value: {
		subjectType: 'me' | 'partner';
		userID?: string;
	};
	onChange: (value: { subjectType: 'me' | 'partner'; userID?: string }) => void;
	hideSubjectSelect?: boolean;
};

export function ConversationPartnerSelect(props: ConversationPartnerSelectProps) {
	const { t } = useTranslation();
	const viewer = useViewer();
	const roles = useViewerRoles();

	const onlyAllowConversationsWithRelatedEmployees = useFeature(
		graphql.FeatureName.ConversationOnlyWithManager,
	);
	const { data: managersData } = graphql.useViewerWithManagersAndManageesQuery();
	const { data: employeesData } = graphql.useOrganizationEmployeesQuery({
		variables: {
			status: graphql.UserStatus.Active,
			first: 5000,
		},
	});

	const managees = (managersData?.viewer.managees ?? []).filter((managee) => !managee.isArchived);
	const managers = (managersData?.viewer.managers ?? []).filter(
		(manager) => !manager.source.isArchived,
	);
	const manageesMap = _.groupBy(managees, 'id');
	const managersMap = _.groupBy(managers, 'source.id');

	const getSuggestions = () => {
		const otherEmployees = employeesData?.users.edges.filter(({ node: user }) => {
			if (user.id === viewer?.id) {
				return false;
			}

			if (props.value.subjectType === 'me') {
				return managersMap[user.id] === undefined;
			}

			return manageesMap[user.id] === undefined;
		});

		const filteredEmployees = onlyAllowConversationsWithRelatedEmployees ? [] : otherEmployees;

		const options = (filteredEmployees ?? []).map(({ node }) => ({
			title: `${node.firstName} ${node.lastName}`,
			value: node.id,
		}));

		const suggestions = {
			title: t('conversations.create.modal.partner.companyLabel'),
			options,
		};

		return suggestions;
	};

	const getEmployees = () => {
		const suggestions = getSuggestions();

		const hasNoPrimaryOptions =
			(props.value.subjectType === 'partner' && managees.length === 0) ||
			(props.value.subjectType === 'me' && managers.length === 0);

		if (hasNoPrimaryOptions) {
			return [suggestions];
		}

		const isPartnerSubject = props.value.subjectType === 'partner';

		const primaryOptions = isPartnerSubject
			? {
					title: t('conversations.create.modal.partner.teamLabel'),
					options: _.sortBy(_.uniqBy(managees, 'id'), (managee) => managee.firstName).map(
						(user) => ({
							title: `${user.firstName} ${user.lastName}`,
							value: user.id,
						}),
					),
				}
			: {
					title: t('conversations.create.modal.partner.managerLabel'),
					options: _.sortBy(
						_.uniqBy(managers, (manager) => manager.source.id),
						(manager) => manager.source.firstName,
					).map((role) => ({
						title: `${role.source.firstName} ${role.source.lastName}`,
						value: role.source.id,
					})),
				};

		return [primaryOptions, suggestions];
	};

	if (roles.loading) {
		return (
			<Flex justifyContent="center">
				<Spinner />
			</Flex>
		);
	}

	return (
		<>
			{!props.hideSubjectSelect && (
				<Subheading mb={2}>
					{props.titlePrefix ? `${props.titlePrefix} ` : ''}
					{t('evaluations.create.modal.partnerLabel')}
				</Subheading>
			)}

			{roles.isManager && !props.moment && !props.hideSubjectSelect && (
				<Box>
					<Paragraph mb={1}>{t('conversations.create.partner.subjectType')}</Paragraph>
					<Flex>
						<Flex
							mr={3}
							alignItems="center"
							onClick={() => props.onChange({ ...props.value, subjectType: 'partner' })}
							style={{ cursor: 'pointer' }}
						>
							<Radio mr={1} checked={props.value.subjectType === 'partner'} onChange={() => {}} />
							<Label>{t('conversations.create.partner.options.partner')}</Label>
						</Flex>
						<Flex
							mr={3}
							alignItems="center"
							onClick={() => props.onChange({ ...props.value, subjectType: 'me' })}
							style={{ cursor: 'pointer' }}
						>
							<Radio mr={1} checked={props.value.subjectType === 'me'} onChange={() => {}} />
							<Label>{t('conversations.create.partner.options.me')}</Label>
						</Flex>
					</Flex>
				</Box>
			)}

			<Paragraph mb={1}>
				{props.value.subjectType === 'me'
					? t('conversations.create.partner.select.manager.label')
					: t('conversations.create.partner.select.managee.label')}
			</Paragraph>
			<Flex>
				<Select
					placeholder={t(`conversations.create.modal.${props.value.subjectType}.empty`)}
					selectedValue={props.value.userID}
					onChange={(userID) => props.onChange({ ...props.value, userID })}
					options={getEmployees()}
				/>
			</Flex>
		</>
	);
}
